import { AgentCtx } from "@closerplatform/spinner-openapi";
import { cast, flow, Instance, SnapshotIn, types } from "mobx-state-tree";

import { Agent } from "../agent";
import {
  servicesGetters,
  agentGroupsStoreGetter,
  tagGroupsStoreGetter,
  tagsStoreGetter
} from "../utils";
import { ITagGroup } from "../tagGroups/TagGroupStore";
import { ITag } from "../tags/TagsStore";
import { uniq } from "lodash";

const Phone = types.model("Phone", {
  region: types.string,
  number: types.string
});

interface IPhone extends Instance<typeof Phone> {}

export type EditableUserProfileFields = {
  firstName?: string;
  lastName?: string;
  phone?: IPhone;
  skills?: string[];
};

/**
 * User profile of currently logged in user. Extends Agent.
 */
export const UserProfile = types
  .compose(
    "UserProfile",
    Agent,
    types.model({
      // role: types.maybe(types.enumeration<UserRole>("Role", Object.values(UserRole))),
      // own properties
      orgId: types.string,
      apiKey: types.string,
      pendingEmail: types.maybe(types.string),
      autoAssignedLimit: types.maybe(types.number),
      phone: types.maybe(Phone),
      importedCalendar: types.maybe(types.string),
      closerCustomerApiKey: types.maybe(types.string)
    })
  )
  .views(servicesGetters)
  .views(agentGroupsStoreGetter)
  .views(tagGroupsStoreGetter)
  .views(tagsStoreGetter)
  .views((self) => ({
    get userTags(): ITag[] {
      const agentGroupTagIds: string[] = self.agentGroupsStore
        .getByIds(self.groups)
        .reduce((acc: string[], group) => {
          acc.push(...group.tagIds);
          return acc;
        }, []);
      const agentGroupTags = self.tagsStore.getFromIds(agentGroupTagIds);

      const userTags = self.tagsStore.getFromIds(self.tags);

      return uniq([...userTags, ...agentGroupTags]);
    },
    get userTagGroups(): ITagGroup[] {
      const agentGroupTagGroupsIds: string[] = self.agentGroupsStore
        .getByIds(self.groups)
        .reduce((acc: string[], group) => {
          acc.push(...group.tagGroupIds);
          return acc;
        }, []);
      const agentGroupTagGroups = self.tagGroupsStore.getFromIds(agentGroupTagGroupsIds);

      const userTagGroups = self.tagGroupsStore.getFromIds(self.tagGroups);

      return uniq([...userTagGroups, ...agentGroupTagGroups]);
    }
  }))
  .actions((self) => {
    /**
     * Request backend to change *current agent's* avatar and update local state
     * @param {File} avatar  FileReader File containing avatar data
     */
    const updateAvatar = flow(function* (avatar: File) {
      try {
        // TODO: API endpoint should return new avatar URL → backend team
        yield self.spinnerService.changeAgentAvatar(avatar);
        const { avatarToken } = yield self.spinnerService.getAgentProfile(self.id);
        self.avatarToken = avatarToken;
      } catch (response) {
        throw Error("Avatar could not be updated");
      }
    });

    /**
     * Request backend to delete *current agent's* avatar and update local state
     */
    const deleteAvatar = flow(function* (fallbackAvatarToken: string | undefined) {
      try {
        yield self.spinnerService.deleteAgentAvatar();
        self.avatarToken = fallbackAvatarToken;
      } catch (response) {
        throw Error("Avatar could not be deleted");
      }
    });

    /**
     * action stub for autoAssignedLimit on profile
     * TODO: is this needed? can we set limit per user?
     */
    const setAutoAssignedLimit = function () {
      throw Error("Not implemented");
    };
    /**
     * Update fields of the profile present in the data param, optionally
     * persising to backend beforehand. (A local equivalent of HTTP PATCH.)
     * TODO: rewrite to use Partial
     * @param {EditableUserProfileFields} data  what to update and with what values
     * @param {boolean} persist  should it be saved to the backend?
     */
    const update = flow(function* (data: EditableUserProfileFields, persist: boolean) {
      try {
        const keysToUpdate = Object.keys(data).filter((key) =>
          self.hasOwnProperty(key)
        ) as ReadonlyArray<keyof EditableUserProfileFields>;
        if (persist) {
          // just send the data and hope the backend will sanitize it
          // TODO: ideally, the endpoint should allow PATCH operations
          // TODO: API endpoint should return profile data → backend team
          // FIXME: setAgentProfile only allows for modyfying current user's profile
          // i don't know what's going on with those undefined names here
          yield self.spinnerService.setAgentProfile({
            firstName: data.firstName !== undefined ? data.firstName : self.firstName!,
            lastName: data.lastName !== undefined ? data.lastName : self.lastName!,
            phone: data.phone
          });
          data = yield self.spinnerService.getAgentProfile(self.id);
        }
        // just update the fields that were meant to change
        keysToUpdate.forEach((key) => {
          const dataForKey = data[key];
          self[key] = cast(dataForKey);
        });
      } catch (e) {
        throw Error("Could not update profile");
      }
    });

    const setImportedCalendar = (calendarName?: string) => {
      self.importedCalendar = calendarName;
    };

    return {
      updateAvatar,
      deleteAvatar,
      setAutoAssignedLimit,
      update,
      setImportedCalendar
    };
  });

export interface IUserProfile extends Instance<typeof UserProfile> {}

export function agentCtxResourceDataToSnapshot(data: AgentCtx): SnapshotIn<typeof UserProfile> {
  const { phone, ...rest } = data;
  return {
    phone: (phone && { region: phone.region, number: phone.number }) || undefined,
    ...rest
  };
}
